// Éléments partagés du module gestion : chemins d'API, actions, statuts d'échéance, sélecteurs.
import { useState, type ReactNode } from "react";
import { ApiError } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { money, date } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Select, StatusChip, useToast, type CheckStatus } from "@core/ui";
import { shiftMonth } from "./util";

/** Chemin d'API du module pour un espace. */
export const gApi = (orgId: string, path: string) => `/api/v1/orgs/${orgId}/gestion${path.startsWith("/") ? path : `/${path}`}`;
/** Chemin d'API côté locataire. */
export const meApi = (leaseId: string, path = "") => `/api/v1/me/gestion/leases/${leaseId}${path}`;
export const docUrl = (id: string, download = false) => `/api/v1/documents/${id}/fichier${download ? "?telecharger=true" : ""}`;

/** Espace courant et chemins du module. */
export function useG(orgIdProp?: string) {
  const org = useOrg();
  const orgId = orgIdProp ?? org.orgId;
  return { ...org, orgId, g: (p: string) => gApi(orgId, p) };
}

export function errMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.fields.length) return e.fields.map((f) => f.message).join(" ");
    return e.message;
  }
  return e instanceof Error ? e.message : "Erreur inattendue.";
}

/** Action asynchrone avec indicateur, message de réussite et rafraîchissement des données du module. */
export function useAction(orgId?: string) {
  const toast = useToast();
  const invalidate = useInvalidate();
  const org = useOrg();
  const id = orgId ?? org.orgId;
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const run = async <T,>(key: string, fn: () => Promise<T>, ok?: string | ((r: T) => string)): Promise<T | null> => {
    setBusy(key);
    setError(null);
    try {
      const r = await fn();
      if (ok) toast(typeof ok === "function" ? ok(r) : ok);
      await invalidate(id ? gApi(id, "") : "/api/v1/me/gestion", "/api/v1/me/gestion");
      return r;
    } catch (e) {
      setError(e);
      toast(errMessage(e), "erreur");
      return null;
    } finally {
      setBusy(null);
    }
  };
  return { busy, error, run, setError };
}

const CALL_STATUS: Record<string, { status: CheckStatus; label: string }> = {
  prevu: { status: "info", label: "Prévu" },
  appele: { status: "info", label: "Appelé" },
  partiel: { status: "a_confirmer", label: "Réglé en partie" },
  paye: { status: "conforme", label: "Réglé" },
  impaye: { status: "anomalie", label: "Impayé" },
  annule: { status: "non_concluant", label: "Annulé" },
};

export function CallStatus({ status }: { status: string }) {
  const s = CALL_STATUS[status] ?? { status: "info" as CheckStatus, label: status };
  return <StatusChip status={s.status} label={s.label} />;
}

const COVERAGE: Record<string, { status: CheckStatus; label: string }> = {
  couverte: { status: "conforme", label: "Couverture déclarée" },
  a_echeance: { status: "a_confirmer", label: "Échéance proche" },
  recue_a_verifier: { status: "info", label: "Reçue, à vérifier" },
  a_corriger: { status: "a_confirmer", label: "À corriger" },
  expiree: { status: "anomalie", label: "Expirée" },
  manquante: { status: "manquant", label: "Manquante" },
};

export function CoverageStatus({ state, label }: { state: string; label?: string }) {
  const s = COVERAGE[state] ?? { status: "info" as CheckStatus, label: state };
  return <StatusChip status={s.status} label={label ?? s.label} />;
}

export const METHOD_LABELS: Record<string, string> = {
  virement: "Virement",
  prelevement: "Prélèvement",
  cheque: "Chèque",
  especes: "Espèces",
  caf: "Aide CAF (tiers payant)",
  autre: "Autre",
};

export const SOURCE_LABELS: Record<string, string> = { manuel: "Saisie", import: "Relevé importé", banque: "Connexion bancaire", caf: "CAF" };

export { currentMonth, shiftMonth } from "./util";

export function MonthPicker({ value, onChange, label = "Mois" }: { value: string; onChange: (m: string) => void; label?: string }) {
  return (
    <div className="row gestion-month">
      <button type="button" className="bv-btn bv-btn-discret bv-btn-petit" onClick={() => onChange(shiftMonth(value, -1))} aria-label="Mois précédent">
        ‹
      </button>
      <label className="gestion-inline-field">
        <span className="sr-only">{label}</span>
        <input type="month" value={value} onChange={(e) => e.target.value && onChange(e.target.value)} />
      </label>
      <button type="button" className="bv-btn bv-btn-discret bv-btn-petit" onClick={() => onChange(shiftMonth(value, 1))} aria-label="Mois suivant">
        ›
      </button>
    </div>
  );
}

export type LeaseRef = {
  id: string;
  property_id: string;
  property_label: string;
  status: string;
  regime: string;
  start_on: string;
  end_on: string | null;
  rent_cents: number;
  charges_cents: number;
  charges_mode: string;
  payment_day: number;
  dpe_class: string | null;
  tenants: string;
};

export function useLeases(orgId?: string) {
  const { g } = useG(orgId);
  return useGet<{ items: LeaseRef[] }>(g("/leases"));
}

export function LeaseSelect({ leases, value, onChange, label = "Bail", activeOnly }: { leases: LeaseRef[]; value: string; onChange: (id: string) => void; label?: string; activeOnly?: boolean }) {
  const list = activeOnly ? leases.filter((l) => l.status === "actif" || l.status === "preavis") : leases;
  return (
    <Select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      options={[{ value: "", label: "Choisir un bail" }, ...list.map((l) => ({ value: l.id, label: `${l.property_label}${l.tenants ? ` — ${l.tenants}` : ""}` }))]}
    />
  );
}

export function DocLink({ id, children = "Ouvrir le PDF" }: { id: string | null | undefined; children?: ReactNode }) {
  if (!id) return null;
  return (
    <a href={docUrl(id)} target="_blank" rel="noreferrer" className="gestion-doc">
      {children}
    </a>
  );
}

export function Amount({ cents, strong }: { cents: number | null | undefined; strong?: boolean }) {
  return <span className={`num gestion-amount ${strong ? "gestion-strong" : ""}`}>{money(cents)}</span>;
}

export function Fictitious() {
  return <Badge tone="attention">Valeur fictive</Badge>;
}

export function OfficialLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer noopener" className="bv-btn bv-btn-secondaire bv-btn-petit">
      {children} <span className="sr-only">(site officiel, nouvel onglet)</span>
    </a>
  );
}

export function PeriodCell({ start, end, label }: { start: string; end: string; label?: string }) {
  return <span title={`du ${date(start)} au ${date(end)}`}>{label ?? `${date(start)} – ${date(end)}`}</span>;
}

export function Criteria({ items }: { items: string[] }) {
  return (
    <ul className="gestion-criteria">
      {items.map((c) => (
        <li key={c}>{c}</li>
      ))}
    </ul>
  );
}

/** Niveau de correspondance d'un rapprochement proposé : jamais une validation. */
export function LevelBadge({ level }: { level: string }) {
  const tone = level === "forte" ? "succes" : level === "moyenne" ? "encre" : "neutre";
  const label = level === "forte" ? "Correspondance forte" : level === "moyenne" ? "Correspondance moyenne" : "Correspondance faible";
  return <Badge tone={tone}>{label}</Badge>;
}

export const RECEIPT_STATUS: Record<string, string> = { emise: "Établie", envoyee: "Envoyée", annulee: "Annulée" };
